/**
 * Emotional Context Content Registry
 *
 * Comprehensive emotional context messages for every phase and moment
 * in the fertility journey. These are never dismissive, never falsely
 * optimistic, and never clinical — they acknowledge real human experience.
 */

export type EnhancedEmotionalContext = {
  moment: string;
  normalization: string;
  physicalContext?: string;
  suggestion?: string;
  communityDataPoint?: string;
};

/**
 * Getting-started step emotional contexts
 */
export const STEP_EMOTIONAL_CONTEXTS: Record<string, EnhancedEmotionalContext> =
  {
    learn: {
      moment: "First steps",
      normalization:
        "Starting this process takes courage. There\u2019s no wrong pace \u2014 some people spend weeks researching, others want to start testing right away. Both are fine.",
      suggestion:
        "Start with what feels manageable. You don\u2019t need to understand everything today.",
      communityDataPoint: "78% of users felt the same way when they started",
    },
    test: {
      moment: "Testing",
      normalization:
        "Waiting for test results can feel heavy. Remember: these numbers are data points, not verdicts. They help your doctor plan the best approach for you.",
      physicalContext:
        "Baseline bloodwork is a simple blood draw. An AFC (antral follicle count) is done via transvaginal ultrasound \u2014 quick and usually painless.",
      communityDataPoint:
        "Most users complete all baseline testing within 2\u20133 weeks",
    },
    clinic: {
      moment: "Choosing a clinic",
      normalization:
        "Choosing a clinic feels like a big decision because it is. But there\u2019s no single \u201Cbest\u201D clinic \u2014 the right one is the one where you feel heard, where the logistics work, and where the clinical approach matches your situation.",
      suggestion:
        "Prepare 3\u20135 questions that matter most to you. The consultation is as much about fit as it is about credentials.",
    },
    prep: {
      moment: "Getting ready",
      normalization:
        "The prep phase can feel like a lot of paperwork and waiting. It\u2019s normal to feel impatient \u2014 you\u2019ve made a decision and want to move forward. The admin work now prevents surprises later.",
      communityDataPoint:
        "Average prep time from first appointment to cycle start: 4\u20136 weeks",
    },
    treatment: {
      moment: "Starting treatment",
      normalization:
        "Starting treatment is a milestone. Whatever mix of excitement, anxiety, and determination you\u2019re feeling \u2014 that\u2019s normal. You\u2019ve prepared for this.",
      physicalContext:
        "Your body will respond to the medications over the coming days. Physical changes are signs that the protocol is working.",
    },
    wait: {
      moment: "The two-week wait",
      normalization:
        "The two-week wait is one of the hardest parts. Every feeling you\u2019re having is valid \u2014 hope, fear, obsession over symptoms. You\u2019re not alone in this.",
      physicalContext:
        "Cramping, spotting, breast tenderness \u2014 these can mean pregnant OR not pregnant. They\u2019re mostly caused by progesterone supplementation. Trust the blood test, not the symptoms.",
      communityDataPoint:
        "You\u2019re one of thousands going through this right now",
    },
    screening: {
      moment: "Screening",
      normalization:
        "The screening process is thorough for a reason \u2014 it\u2019s about your safety and informed consent. It\u2019s normal to feel scrutinized, but every question has a purpose.",
    },
    match: {
      moment: "Waiting to match",
      normalization:
        "Waiting to be matched can feel uncertain. The timeline varies widely and that\u2019s out of your control. Focus on what you can do in the meantime.",
    },
  };

/**
 * Treatment phase emotional contexts (day-specific)
 */
export const TREATMENT_PHASE_CONTEXTS: Record<
  string,
  EnhancedEmotionalContext[]
> = {
  baseline: [
    {
      moment: "Baseline check",
      normalization:
        "Your baseline appointment confirms everything is clear to start. If something needs adjusting, that\u2019s not a setback \u2014 it\u2019s your clinic being careful.",
    },
  ],
  stimulation: [
    {
      moment: "Day 1 of stimulation",
      normalization:
        "Day 1. You\u2019re doing this. The first injection is the hardest \u2014 it gets easier. Most women say by day 3 it becomes routine.",
      physicalContext:
        "You won\u2019t feel much yet. The medications take a few days to start working.",
      suggestion:
        "Ice the injection site for 30 seconds before \u2014 it helps.",
    },
    {
      moment: "Days 5\u20137 of stimulation",
      normalization:
        "You might be feeling bloated, moody, and tired. This is your body responding to the medications \u2014 rising estrogen causes all of these. It\u2019s a sign things are working.",
      physicalContext:
        "Your ovaries are growing multiple follicles. The bloating is real and physical \u2014 it\u2019s not in your head.",
      suggestion: "Loose clothing, gentle walks, and plenty of water help.",
    },
    {
      moment: "Days 8\u201310 of stimulation",
      normalization:
        "The frequent monitoring visits can feel exhausting. You\u2019re in the home stretch. Your clinic is tracking follicle growth closely to time your trigger perfectly.",
      physicalContext:
        "Your ovaries may feel heavy or tender. Avoid high-impact exercise and twisting movements to prevent ovarian torsion.",
    },
  ],
  trigger: [
    {
      moment: "Trigger day",
      normalization:
        "Tonight\u2019s injection triggers the final maturation of your eggs. Timing is critical \u2014 your retrieval is set for exactly 34\u201336 hours from now. Set your alarm.",
      suggestion:
        "Confirm your retrieval time with your clinic. Arrange your ride. Prep comfortable clothes for tomorrow.",
    },
  ],
  retrieval: [
    {
      moment: "Morning of retrieval",
      normalization:
        "Today is the day. The procedure takes about 20 minutes under light sedation. You won\u2019t feel anything. When you wake up, it\u2019s done. Have someone drive you home.",
    },
    {
      moment: "After retrieval",
      normalization:
        "You did it. Rest today. Cramping and bloating are normal. Your eggs are now with the embryology team. For egg freezing, you\u2019ll know your mature egg count today or tomorrow.",
      physicalContext:
        "Mild to moderate cramping is expected. Take the pain medication your clinic prescribed. Apply a heating pad if it helps.",
    },
  ],
  fertilization: [
    {
      moment: "Fertilization report",
      normalization:
        "The fertilization report tells you how many eggs fertilized normally. It\u2019s normal for not all eggs to fertilize \u2014 a 70\u201380% fertilization rate with ICSI is typical.",
    },
  ],
  culture: [
    {
      moment: "Embryo development",
      normalization:
        "The days between retrieval and transfer (or freeze) can feel agonizing. Your embryos are developing and the lab is watching them. Not all embryos make it to day 5 \u2014 this is a normal part of natural selection, not a failure.",
      communityDataPoint:
        "Typically 30\u201350% of fertilized eggs reach blastocyst stage",
    },
  ],
  transfer: [
    {
      moment: "Transfer day",
      normalization:
        "Transfer is gentler than retrieval \u2014 no sedation needed, usually painless. The embryo is placed via a thin catheter. You\u2019ll see it on the ultrasound screen. Then begins the wait.",
    },
  ],
  tww: [
    {
      moment: "TWW Days 1\u20133",
      normalization:
        "The hardest part of IVF isn\u2019t the injections or the procedures \u2014 it\u2019s the waiting. Right now, if implantation is going to happen, the embryo is beginning to attach to your uterine lining. You can\u2019t feel it, and that\u2019s normal.",
    },
    {
      moment: "TWW Days 7\u201310",
      normalization:
        "You\u2019re probably symptom-spotting. Cramping, spotting, sore breasts \u2014 these can all mean pregnant OR not pregnant. They\u2019re mostly caused by the progesterone you\u2019re taking. Trust the blood test, not the symptoms.",
    },
    {
      moment: "TWW Days 12\u201314",
      normalization:
        "Almost there. The anticipation is brutal. Whatever the result, you\u2019ve done everything you could. The blood test gives a definitive answer \u2014 no more guessing.",
    },
  ],
  beta: [
    {
      moment: "Negative result",
      normalization:
        "This is devastating, and there are no words that make it less so. Take the time you need. When you\u2019re ready, know that a failed cycle provides your clinic with valuable information about how your body responds \u2014 it\u2019s not starting over, it\u2019s building on data.",
    },
    {
      moment: "Positive result",
      normalization:
        "Congratulations. Take a breath. The first beta is the beginning \u2014 your clinic will monitor rising hCG levels over the next few weeks. Today, just feel this.",
    },
  ],
  complete: [
    {
      moment: "Cycle complete",
      normalization:
        "Whatever you\u2019re feeling right now \u2014 relief, exhaustion, pride, uncertainty \u2014 it\u2019s all valid. You\u2019ve done something meaningful for your future self.",
    },
    {
      moment: "Starting a second cycle",
      normalization:
        "You\u2019ve been through this before, and that experience is valuable. Your clinic may adjust your protocol based on your first cycle\u2019s response. You know what to expect now.",
    },
  ],
};

/**
 * Get emotional context for a step
 */
export function getStepEmotionalContext(
  stepId: string,
): EnhancedEmotionalContext | null {
  return STEP_EMOTIONAL_CONTEXTS[stepId] ?? null;
}

/**
 * Get treatment phase emotional context by day
 */
export function getTreatmentEmotionalContext(
  phase: string,
  dayInPhase?: number,
): EnhancedEmotionalContext | null {
  const contexts = TREATMENT_PHASE_CONTEXTS[phase];
  if (!contexts || contexts.length === 0) return null;

  // For phases with multiple day-specific contexts, select by day
  if (phase === "stimulation" && dayInPhase !== undefined) {
    if (dayInPhase <= 2) return contexts[0] ?? null;
    if (dayInPhase <= 7) return contexts[1] ?? null;
    return contexts[2] ?? null;
  }

  if (phase === "tww" && dayInPhase !== undefined) {
    if (dayInPhase <= 3) return contexts[0] ?? null;
    if (dayInPhase <= 10) return contexts[1] ?? null;
    return contexts[2] ?? null;
  }

  if (phase === "retrieval" && dayInPhase !== undefined) {
    return dayInPhase === 0 ? (contexts[0] ?? null) : (contexts[1] ?? null);
  }

  return contexts[0] ?? null;
}
